import { useColors } from "@/constants/theme";
import { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import Svg, { Circle, Line, Polygon, Text as SvgText } from "react-native-svg";

export interface RadarDatum {
  label: string;
  value: number;
}

interface ThinkingRadarProps {
  data: RadarDatum[];
  size?: number;
  color?: string;
}

/**
 * 思考タイプの傾向を多角形（レーダーチャート）で可視化する。
 * 3軸未満では多角形が成立しないため、その場合は呼び出し側で出し分ける。
 */
export default function ThinkingRadar({
  data,
  size = 260,
  color,
}: ThinkingRadarProps) {
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const lineColor = color ?? c.primary;
  const axisCount = data.length;
  const center = size / 2;
  const radius = size / 2 - 44; // ラベル分の余白
  const maxValue = Math.max(...data.map((d) => d.value), 1);
  const rings = [0.34, 0.67, 1];

  const pointAt = (index: number, ratio: number) => {
    const angle = (Math.PI * 2 * index) / axisCount - Math.PI / 2;
    return {
      x: center + radius * ratio * Math.cos(angle),
      y: center + radius * ratio * Math.sin(angle),
    };
  };

  const polygonPoints = (ratio: number) =>
    data
      .map((_, index) => {
        const { x, y } = pointAt(index, ratio);
        return `${x},${y}`;
      })
      .join(" ");

  const dataPoints = data
    .map((datum, index) => {
      const ratio = datum.value / maxValue;
      const { x, y } = pointAt(index, ratio);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <View style={styles.container}>
      <Svg width={size} height={size}>
        {/* グリッド（同心多角形） */}
        {rings.map((ring) => (
          <Polygon
            key={ring}
            points={polygonPoints(ring)}
            fill="none"
            stroke={c.border}
            strokeWidth={1}
          />
        ))}

        {/* 軸線とラベル */}
        {data.map((datum, index) => {
          const outer = pointAt(index, 1);
          return (
            <Line
              key={`axis-${datum.label}`}
              x1={center}
              y1={center}
              x2={outer.x}
              y2={outer.y}
              stroke={c.border}
              strokeWidth={1}
            />
          );
        })}

        {/* データ多角形 */}
        <Polygon
          points={dataPoints}
          fill={`${lineColor}33`}
          stroke={lineColor}
          strokeWidth={2}
        />
        {data.map((datum, index) => {
          const ratio = datum.value / maxValue;
          const { x, y } = pointAt(index, ratio);
          return (
            <Circle
              key={`dot-${datum.label}`}
              cx={x}
              cy={y}
              r={3}
              fill={lineColor}
            />
          );
        })}

        {/* 頂点ラベル */}
        {data.map((datum, index) => {
          const labelPos = pointAt(index, 1.16);
          return (
            <SvgText
              key={`label-${datum.label}`}
              x={labelPos.x}
              y={labelPos.y}
              fill={c.textSecondary}
              fontSize={11}
              fontWeight="700"
              textAnchor="middle"
              alignmentBaseline="middle"
            >
              {datum.label}
            </SvgText>
          );
        })}
      </Svg>
      <Text style={styles.caption}>使った回数で見るあなたの思考バランス</Text>
    </View>
  );
}

const createStyles = (c: ReturnType<typeof useColors>) => StyleSheet.create({
  container: {
    alignItems: "center",
    paddingVertical: 8,
  },
  caption: {
    marginTop: 8,
    color: c.textMuted,
    fontSize: 12,
  },
});
